import type { ExecuteAction } from '@mls-wallet/shared-types';
import type { StartExecutionOptions } from './execution-context';
import type {
  ExecutionRequestResult,
  ProposalExecutionType,
} from './execution-state';

type ExecuteProposal = (input: {
  proposalId: string;
  action: ExecuteAction;
}) => Promise<ExecutionRequestResult>;

interface StartProposalBatchExecutionOptions {
  proposalIds: string[];
  action: ExecuteAction;
  proposalType: ProposalExecutionType;
  startExecution: (options: StartExecutionOptions) => string;
  executeProposal: ExecuteProposal;
  /** Runs once per unique proposal execution, not once after the whole batch. */
  onSuccess?: StartExecutionOptions['onSuccess'];
  /** Revalidates the batch after a competing execution wins one proposal. */
  onAlreadyExecuting?: (
    proposalId: string
  ) => void | Promise<unknown> | undefined;
}

/**
 * Register the entire batch with the execution tray before any request settles.
 *
 * Each proposal gets its own tray entry so partial failures remain visible. The
 * synchronous loop also prevents navigation from unmounting the caller before
 * the remaining requests are registered. Requests then settle one at a time so
 * a shared SWR mutation cannot discard an earlier proposal's result. Returned
 * execution IDs follow the first occurrence of each proposal ID.
 */
export function startProposalBatchExecution({
  proposalIds,
  action,
  proposalType,
  startExecution,
  executeProposal,
  onSuccess,
  onAlreadyExecuting,
}: StartProposalBatchExecutionOptions): string[] {
  // Set preserves first-seen order while preventing duplicate requests from
  // creating more than one GuardianTransaction for the same proposal.
  const uniqueProposalIds = [...new Set(proposalIds)];
  let requestQueue = Promise.resolve();

  return uniqueProposalIds.map((proposalId) => {
    const request = () => {
      // The account and member batch callers share one SWR mutation trigger.
      // Waiting for its previous call to settle keeps every result observable.
      const requestResult = requestQueue.then(() =>
        executeProposal({ proposalId, action })
      );
      requestQueue = requestResult.then(
        () => undefined,
        () => undefined
      );
      return requestResult;
    };

    // Do not await here: all tray entries must be registered synchronously
    // before navigation can unmount the caller.
    return startExecution({
      executionTarget: 'proposal',
      executionTargetId: proposalId,
      proposalType,
      request,
      onSuccess,
      onAlreadyExecuting: () => onAlreadyExecuting?.(proposalId),
    });
  });
}
